import { useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

function ScenarioPlanning() {
  const [scenarios] = useState({
    likely: {
      revenue: 1200000,
      costs: 900000,
      profit: 300000,
      growth: 15
    },
    desired: {
      revenue: 1500000,
      costs: 1000000,
      profit: 500000,
      growth: 25
    }
  });

  const comparisonData = [
    {
      metric: 'Revenue',
      Likely: scenarios.likely.revenue,
      Desired: scenarios.desired.revenue,
      gap: scenarios.desired.revenue - scenarios.likely.revenue
    },
    {
      metric: 'Costs',
      Likely: scenarios.likely.costs,
      Desired: scenarios.desired.costs,
      gap: scenarios.desired.costs - scenarios.likely.costs
    },
    {
      metric: 'Profit',
      Likely: scenarios.likely.profit,
      Desired: scenarios.desired.profit,
      gap: scenarios.desired.profit - scenarios.likely.profit
    }
  ];

  const recommendations = [
    {
      title: 'Increase Marketing Efficiency',
      description: 'Optimize ad spend across channels to reduce customer acquisition cost by 20%',
      impact: '+$50,000'
    },
    {
      title: 'Expand Product Line',
      description: 'Launch 3 new product variations to capture additional market share',
      impact: '+$100,000'
    },
    {
      title: 'Improve Customer Retention',
      description: 'Implement loyalty program to increase repeat purchase rate by 15%',
      impact: '+$75,000'
    }
  ];

  return (
    <div className="p-6">
      <div className="max-w-7xl mx-auto">
        <h1 className="text-2xl font-bold text-gray-900 mb-8">Scenario Planning</h1>

        {/* Scenario Comparison */}
        <div className="bg-white rounded-lg p-6 shadow-sm mb-8">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Scenario Comparison</h2>
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={comparisonData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="metric" />
                <YAxis />
                <Tooltip />
                <Bar dataKey="Likely" fill="#818CF8" name="Likely Scenario" />
                <Bar dataKey="Desired" fill="#C084FC" name="Desired Scenario" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Growth Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
          <div className="bg-white rounded-lg p-6 shadow-sm">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Likely Scenario</h3>
            <dl className="grid grid-cols-1 gap-4">
              <div>
                <dt className="text-sm font-medium text-gray-500">Revenue Growth</dt>
                <dd className="mt-1 text-2xl font-semibold text-gray-900">{scenarios.likely.growth}%</dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">Projected Revenue</dt>
                <dd className="mt-1 text-2xl font-semibold text-gray-900">${scenarios.likely.revenue.toLocaleString()}</dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">Projected Profit</dt>
                <dd className="mt-1 text-2xl font-semibold text-gray-900">${scenarios.likely.profit.toLocaleString()}</dd>
              </div>
            </dl>
          </div>

          <div className="bg-white rounded-lg p-6 shadow-sm">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Desired Scenario</h3>
            <dl className="grid grid-cols-1 gap-4">
              <div>
                <dt className="text-sm font-medium text-gray-500">Revenue Growth</dt>
                <dd className="mt-1 text-2xl font-semibold text-gray-900">{scenarios.desired.growth}%</dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">Target Revenue</dt>
                <dd className="mt-1 text-2xl font-semibold text-gray-900">${scenarios.desired.revenue.toLocaleString()}</dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">Target Profit</dt>
                <dd className="mt-1 text-2xl font-semibold text-gray-900">${scenarios.desired.profit.toLocaleString()}</dd>
              </div>
            </dl>
          </div>
        </div>

        {/* Recommendations */}
        <div className="bg-white rounded-lg p-6 shadow-sm">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Recommendations to Bridge the Gap</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {recommendations.map((rec, index) => (
              <div key={index} className="bg-gray-50 rounded-lg p-4">
                <h3 className="font-medium text-gray-900">{rec.title}</h3>
                <p className="mt-2 text-sm text-gray-500">{rec.description}</p>
                <p className="mt-2 text-sm font-medium text-green-600">{rec.impact}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export default ScenarioPlanning;